import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Space, Tag, Typography } from '@app/ui';

import { baseSportsApi, API_URL } from '@app/utils/sportsApi';
import { RefEndpoint } from './apiReferenceModel';

const { Text } = Typography;

type TryItConsoleProps = {
    endpoint: RefEndpoint;
    /** Display name of the client requests run as. */
    clientName: string;
};

type ExecutionResult = {
    status: number;
    body: string;
    ok: boolean;
    /** Wall-clock duration of the request in milliseconds. */
    ms: number;
};

/** Pretty-print a response body as JSON, falling back to the raw text. */
const pretty = (text: string): string => {
    if (!text) return '';
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        return text;
    }
};

/**
 * Interactive "TRY IT" console for the API Reference endpoint view (SP-8216).
 *
 * Adapted from TryItOut.tsx: fires a REAL authenticated request through
 * `baseSportsApi` (the wretch middleware injects the bearer token, consumer-key
 * header, and clientId), then renders the live HTTP status, a timing readout in
 * ms, and the response JSON — all with our design-system components and brand
 * `var(--*)` tokens. GET shows a 2-col grid of editable inputs for path/query
 * params; POST shows a JSON body editor prefilled from the request example.
 */
const TryItConsole = ({ endpoint, clientName }: TryItConsoleProps) => {
    const { t } = useTranslation('settings');
    const { method, path } = endpoint;
    const verb = method.toLowerCase();
    const sendsBody = verb === 'post' || verb === 'put' || verb === 'patch';

    // Path + query params are editable; header params are excluded.
    const editableParams = useMemo(
        () =>
            endpoint.params.filter(p => p.loc === 'path' || p.loc === 'query'),
        [endpoint.params],
    );

    const initialBody = useMemo(
        () =>
            endpoint.requestBodyExample !== undefined
                ? JSON.stringify(endpoint.requestBodyExample, null, 2)
                : '{}',
        [endpoint.requestBodyExample],
    );

    const [paramValues, setParamValues] = useState<Record<string, string>>({});
    const [body, setBody] = useState(initialBody);
    const [bodyError, setBodyError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ExecutionResult | null>(null);

    // Reset all interactive state when the endpoint route changes.
    useEffect(() => {
        setParamValues({});
        setBody(initialBody);
        setBodyError(null);
        setResult(null);
    }, [endpoint.operationId, initialBody]);

    const paramKey = (name: string, loc: string) => `${loc}:${name}`;

    const setParam = (key: string, value: string) =>
        setParamValues(prev => ({ ...prev, [key]: value }));

    // Substitute {name} path params and collect non-empty query params.
    const buildRequest = () => {
        const rel = path.replace(/^\/v1/, '');
        let resolvedPath = rel;
        const query: Record<string, string> = {};

        editableParams.forEach(param => {
            if (!param.name) return;
            const raw = paramValues[paramKey(param.name, param.loc)];
            if (raw === undefined || raw === '') return;
            if (param.loc === 'path') {
                resolvedPath = resolvedPath.replace(
                    `{${param.name}}`,
                    encodeURIComponent(raw),
                );
            } else if (param.loc === 'query') {
                query[param.name] = raw;
            }
        });

        return { resolvedPath, query };
    };

    // A human-readable preview of the resolved request line, e.g.
    // `GET /reports/123?currency=USD`. Shown above Send so the user can confirm
    // exactly what will be sent before a live call runs.
    const requestPreview = useMemo(() => {
        const { resolvedPath, query } = buildRequest();
        const qs = Object.entries(query)
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
        return `${method.toUpperCase()} ${resolvedPath}${qs ? `?${qs}` : ''}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [method, path, paramValues, editableParams]);

    const execute = async () => {
        setBodyError(null);
        setResult(null);

        let bodyObj: object = {};
        if (sendsBody) {
            try {
                bodyObj = body.trim() === '' ? {} : JSON.parse(body);
            } catch {
                setBodyError(t('userSettings.apiReference.invalidJson'));
                return;
            }
        }

        const { resolvedPath, query } = buildRequest();

        setLoading(true);
        const start =
            typeof performance !== 'undefined' && performance.now
                ? performance.now()
                : Date.now();
        try {
            const w = baseSportsApi.url(API_URL).url(resolvedPath).query(query);

            let resPromise: Promise<Response>;
            if (verb === 'get') {
                resPromise = w.get().res();
            } else if (verb === 'delete') {
                resPromise = w.delete().res();
            } else if (verb === 'post') {
                resPromise = w.json(bodyObj).post().res();
            } else if (verb === 'put') {
                resPromise = w.json(bodyObj).put().res();
            } else if (verb === 'patch') {
                resPromise = w.json(bodyObj).patch().res();
            } else {
                resPromise = w.get().res();
            }

            const res = await resPromise;
            const text = await res.text();
            const end =
                typeof performance !== 'undefined' && performance.now
                    ? performance.now()
                    : Date.now();
            setResult({
                status: res.status,
                body: pretty(text),
                ok: res.status >= 200 && res.status < 300,
                ms: Math.round(end - start),
            });
        } catch (err: any) {
            const end =
                typeof performance !== 'undefined' && performance.now
                    ? performance.now()
                    : Date.now();
            const rawBody =
                err?.text ?? err?.message ?? String(err ?? 'Request failed');
            setResult({
                status: err?.status ?? 0,
                body: pretty(rawBody),
                ok: false,
                ms: Math.round(end - start),
            });
        } finally {
            setLoading(false);
        }
    };

    // GET runs immediately; mutating verbs (POST/PUT/PATCH/DELETE) ask for an
    // explicit confirmation first since the request hits the live API as the
    // current client.
    const onSend = () => {
        if (!sendsBody && verb !== 'delete') {
            execute();
            return;
        }
        Modal.confirm({
            title: t('userSettings.apiReference.confirmTitle'),
            content: t('userSettings.apiReference.confirmBody', {
                method: method.toUpperCase(),
                client: clientName,
            }),
            okText: t('userSettings.apiReference.send'),
            cancelText: t('userSettings.apiReference.cancel'),
            onOk: execute,
        });
    };

    return (
        <div className="api-ref-tryit" data-test="api-ref-tryit">
            <div className="api-ref-tryit__head">
                <Space size="small" align="center" wrap>
                    <Text strong className="api-ref-tryit__title">
                        {t('userSettings.apiReference.tryIt')}
                    </Text>
                    <Text strong className="api-ref-tryit__runas">
                        {t('userSettings.apiReference.runsAs', {
                            client: clientName,
                        })}
                    </Text>
                </Space>
                <Button
                    data-test="api-ref-tryit-send"
                    variant="primary"
                    size="small"
                    loading={loading}
                    onClick={onSend}
                >
                    {t('userSettings.apiReference.send')}
                </Button>
            </div>

            <div
                className="api-ref-tryit__preview"
                data-test="api-ref-tryit-preview"
            >
                <Text code className="api-ref-tryit__preview-line">
                    {requestPreview}
                </Text>
                <Text type="secondary" className="api-ref-tryit__hint">
                    {t('userSettings.apiReference.hint')}
                </Text>
            </div>

            <div className="api-ref-tryit__body">
                {sendsBody ? (
                    <div data-test="api-ref-tryit-bodyeditor">
                        <Text type="secondary">
                            {t('userSettings.apiReference.requestBody')}
                        </Text>
                        <Input.TextArea
                            value={body}
                            rows={8}
                            spellCheck={false}
                            onChange={e => setBody(e.target.value)}
                        />
                        {bodyError ? (
                            <div
                                className="api-ref-tryit__error"
                                data-test="api-ref-tryit-bodyerror"
                            >
                                {bodyError}
                            </div>
                        ) : null}
                    </div>
                ) : editableParams.length > 0 ? (
                    <div className="api-ref-tryit__params">
                        {editableParams.map(param => {
                            const key = paramKey(param.name, param.loc);
                            return (
                                <div
                                    key={key}
                                    className="api-ref-tryit__param"
                                    data-test="api-ref-tryit-param"
                                >
                                    <Space size={4} wrap>
                                        <Text code>{param.name}</Text>
                                        <Tag>{param.loc}</Tag>
                                        {param.required ? (
                                            <Tag color="red">
                                                {t(
                                                    'userSettings.apiDocumentation.required',
                                                )}
                                            </Tag>
                                        ) : null}
                                    </Space>
                                    <Input
                                        size="small"
                                        value={paramValues[key] ?? ''}
                                        placeholder={param.placeholder}
                                        onChange={e =>
                                            setParam(key, e.target.value)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Text type="secondary">
                        {t('userSettings.apiReference.noParameters')}
                    </Text>
                )}
            </div>

            <div
                className="api-ref-tryit__result"
                data-test="api-ref-tryit-response"
                aria-live="polite"
            >
                {result ? (
                    <>
                    <Space size="small" align="center" wrap>
                        <Text type="secondary">
                            {t('userSettings.apiReference.status')}
                        </Text>
                        <Tag
                            color={result.ok ? 'green' : undefined}
                            className={
                                result.ok ? undefined : 'api-ref-tag--danger'
                            }
                            data-test="api-ref-tryit-status"
                        >
                            {result.status}
                        </Tag>
                        <Text type="secondary" data-test="api-ref-tryit-timing">
                            {t('userSettings.apiReference.timing', {
                                ms: result.ms,
                            })}
                        </Text>
                    </Space>
                    <pre className="api-ref-code__pre">{result.body}</pre>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default TryItConsole;
